import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { EventDto } from '@/types/dtos/events/response/event.dto.ts';
import type { CreateEventModeDto } from '@/types/dtos/events/request/create-event-mode.dto.ts';
import { GET_EVENT_QUERY_KEY } from '@/state/queries/events/use-get-event.ts';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors';
import { invalidatePublicationStateQueries } from '@/state/queries/events/use-publication-state';

export const useCreateEventModeMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateEventModeDto) => {
      const token = await getToken();
      return createEventMode(params, token ?? '');
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_SESSIONS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_FLOWS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY],
        }),
        invalidatePublicationStateQueries(queryClient),
      ]);
    },
  });
};

const createEventMode = async (params: CreateEventModeDto, token: string): Promise<EventDto> => {
  const { id, ...rest } = params;
  const url = `${import.meta.env.VITE_API_URL}/events/${id}/mode`;
  return sendJSONRequest({ url, method: METHOD.POST, body: rest, token });
};
